/**
 * GovernanceOnboardingPage
 * Rota: /governance/onboarding
 *
 * Wizard de 4 passos para novos clientes pós-ativação no Marketplace.
 * Idioma: PT / EN via GovernanceLangContext + getGovernanceT()
 *
 * Passos:
 *   1. Dados da organização
 *   2. Plano ativo
 *   3. Convidar usuários
 *   4. Conclusão
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, ChevronRight, ChevronLeft, Plus, Trash2, Building2, CreditCard, Users, Rocket, Globe } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { AZURE } from '../../services/api';
import { useGovernanceLang } from '../../contexts/GovernanceLangContext';
import { getGovernanceT } from '../../i18n/governance';

// ─── Design system ────────────────────────────────────────────────────────────
const G    = '#064e3b';
const GOLD = '#b48c2c';
const BG   = '#f8fafc';

const TOTAL_STEPS = 4;

// ─── Step Progress Bar ────────────────────────────────────────────────────────
const StepBar = ({ current, labels }) => (
  <div className="flex items-center gap-0 mb-8">
    {labels.map((label, idx) => {
      const step  = idx + 1;
      const done  = step < current;
      const active = step === current;
      return (
        <React.Fragment key={step}>
          <div className="flex flex-col items-center">
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-all"
              style={{
                background: done ? G : active ? G : '#e5e7eb',
                color: done || active ? '#fff' : '#6b7280',
                boxShadow: active ? `0 0 0 3px ${GOLD}44` : 'none',
              }}
            >
              {done ? <CheckCircle className="w-4 h-4" /> : step}
            </div>
            <span className="text-[11px] mt-1 font-medium" style={{ color: active ? G : '#9ca3af' }}>
              {label}
            </span>
          </div>
          {idx < labels.length - 1 && (
            <div
              className="flex-1 h-0.5 mx-1 mb-5 transition-all"
              style={{ background: done ? G : '#e5e7eb' }}
            />
          )}
        </React.Fragment>
      );
    })}
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const GovernanceOnboardingPage = () => {
  const { user }   = useAuth();
  const navigate   = useNavigate();
  const { lang, setLang } = useGovernanceLang();
  const t          = getGovernanceT(lang).governance.onboarding;

  const [step, setStep]         = useState(1);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState(null);
  const [success, setSuccess]   = useState(null);

  // Step 1
  const [orgName,    setOrgName]    = useState('');
  const [orgSector,  setOrgSector]  = useState('');
  const [orgCountry, setOrgCountry] = useState('');

  // Step 2
  const [plan, setPlan] = useState(null);

  // Step 3
  const [invites, setInvites] = useState([{ email: '', role: 'viewer' }]);

  // Fetch plan info on mount
  useEffect(() => {
    AZURE.get('/marketplace/subscription').then(res => {
      setPlan(res.data?.data || res.data || null);
    }).catch(() => setPlan(null));

    // Pre-fill org name from user context if available
    if (user?.institution_name) setOrgName(user.institution_name);
  }, [user]);

  const stepLabels = [t.step1Label, t.step2Label, t.step3Label, t.step4Label];

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleNext = async () => {
    setError(null);
    if (step === 1) {
      if (!orgName.trim()) return;
      // Optionally save org data
      setSaving(true);
      try {
        await AZURE.patch('/marketplace/tenant-profile', {
          name: orgName, sector: orgSector, country: orgCountry,
        });
      } catch {
        // Non-blocking — profile update failure does not block wizard
      } finally {
        setSaving(false);
      }
    }
    if (step === 3) {
      // Send invites
      const valid = invites.filter(i => i.email.trim());
      if (valid.length > 0) {
        setSaving(true);
        try {
          await AZURE.post('/governance/users/invite-bulk', { invites: valid });
          setSuccess(t.inviteSent);
          setTimeout(() => setSuccess(null), 3000);
        } catch {
          setError(t.inviteError);
          setSaving(false);
          return;
        } finally {
          setSaving(false);
        }
      }
    }
    setStep(s => Math.min(s + 1, TOTAL_STEPS));
  };

  const handleBack = () => {
    setError(null);
    setStep(s => Math.max(s - 1, 1));
  };

  const handleFinish = () => navigate('/governance/overview');

  const addInviteRow = () => setInvites(prev => [...prev, { email: '', role: 'viewer' }]);
  const removeInviteRow = (idx) => setInvites(prev => prev.filter((_, i) => i !== idx));
  const updateInvite = (idx, field, val) =>
    setInvites(prev => prev.map((row, i) => i === idx ? { ...row, [field]: val } : row));

  // ── Step content ──────────────────────────────────────────────────────────
  const stepIcons = [Building2, CreditCard, Users, Rocket];
  const StepIcon = stepIcons[step - 1];
  const stepTitles = [t.step1Title, t.step2Title, t.step3Title, t.step4Title];
  const stepDescs  = [t.step1Desc,  t.step2Desc,  t.step3Desc,  t.step4Desc];

  const roleOpts = Object.entries(t.roleOptions || {}).map(([v, l]) => ({ value: v, label: l }));
  const sectorOpts = Object.entries(t.sectorOptions || {}).map(([v, l]) => ({ value: v, label: l }));

  return (
    <div className="min-h-screen flex flex-col items-center justify-start pt-10 px-4" style={{ background: BG }}>
      {/* Lang toggle */}
      <div className="w-full max-w-xl flex justify-end mb-2">
        <button
          onClick={() => setLang(lang === 'en' ? 'pt' : 'en')}
          className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-gray-200 bg-white hover:bg-gray-50 text-gray-600 font-medium transition"
        >
          <Globe className="w-3.5 h-3.5" />
          {lang === 'en' ? 'PT' : 'EN'}
        </button>
      </div>

      {/* Card */}
      <div className="w-full max-w-xl bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        {/* Header banner */}
        <div className="h-2" style={{ background: `linear-gradient(90deg, ${G}, ${GOLD})` }} />

        <div className="p-8">
          {/* Logo + title */}
          <div className="flex items-center gap-3 mb-6">
            <img src="/vukz-logo.png" alt="VUKZ" className="w-10 h-10 rounded-xl object-contain bg-gray-50 border border-gray-200 p-0.5"
              onError={e => { e.target.style.display = 'none'; }} />
            <div>
              <h1 className="text-xl font-extrabold" style={{ color: G }}>{t.title}</h1>
              <p className="text-xs text-gray-500">{t.subtitle}</p>
            </div>
          </div>

          {/* Progress */}
          <StepBar current={step} labels={stepLabels} />

          {/* Step header */}
          <div className="flex items-start gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: `${G}15` }}>
              <StepIcon className="w-5 h-5" style={{ color: G }} />
            </div>
            <div>
              <h2 className="font-extrabold text-gray-900 text-lg leading-tight">{stepTitles[step - 1]}</h2>
              <p className="text-sm text-gray-500 mt-0.5">{stepDescs[step - 1]}</p>
            </div>
          </div>

          {/* Step body */}
          {step === 1 && (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">{t.orgNameLabel} *</label>
                <input
                  type="text"
                  value={orgName}
                  onChange={e => setOrgName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2"
                  style={{ '--tw-ring-color': GOLD }}
                  placeholder="VUKZ Inc."
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">{t.orgSectorLabel}</label>
                  <select value={orgSector} onChange={e => setOrgSector(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none">
                    <option value="">—</option>
                    {sectorOpts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">{t.orgCountryLabel}</label>
                  <input type="text" value={orgCountry} onChange={e => setOrgCountry(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none"
                    placeholder="Brazil" />
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-3">
              {plan ? (
                <div className="rounded-xl border border-gray-200 p-5 bg-gray-50">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-bold uppercase tracking-wide text-gray-500">{t.planLabel}</span>
                    <span className="text-xs px-2 py-1 rounded-full font-bold text-white"
                      style={{ background: G }}>{plan.planId || plan.plan_id || '—'}</span>
                  </div>
                  <div className="text-2xl font-extrabold mb-1" style={{ color: G }}>
                    {plan.planName || plan.plan_name || plan.planId || '—'}
                  </div>
                  {(plan.expiry_date || plan.saasSubscriptionStatus) && (
                    <p className="text-xs text-gray-500">
                      {plan.saasSubscriptionStatus ? `Status: ${plan.saasSubscriptionStatus}` : ''}
                      {plan.expiry_date ? ` · ${t.planActiveUntil}: ${plan.expiry_date}` : ''}
                    </p>
                  )}
                </div>
              ) : (
                <div className="rounded-xl border border-gray-200 p-5 text-center text-sm text-gray-400">
                  {t.planLabel}: —
                </div>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-3">
              {invites.map((row, idx) => (
                <div key={idx} className="flex gap-2 items-center">
                  <input
                    type="email"
                    value={row.email}
                    onChange={e => updateInvite(idx, 'email', e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none"
                    placeholder={t.emailPlaceholder}
                  />
                  <select value={row.role} onChange={e => updateInvite(idx, 'role', e.target.value)}
                    className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none w-44">
                    {roleOpts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                  {invites.length > 1 && (
                    <button onClick={() => removeInviteRow(idx)}
                      className="p-2 text-gray-400 hover:text-rose-500 transition">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
              <button onClick={addInviteRow}
                className="flex items-center gap-1.5 text-sm font-semibold mt-1 transition"
                style={{ color: G }}>
                <Plus className="w-4 h-4" /> {t.addUserBtn}
              </button>
            </div>
          )}

          {step === 4 && (
            <div className="text-center py-4">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                style={{ background: `${G}15` }}>
                <CheckCircle className="w-9 h-9" style={{ color: G }} />
              </div>
              <h3 className="text-xl font-extrabold mb-2" style={{ color: G }}>{t.step4Title}</h3>
              <p className="text-gray-500 text-sm max-w-xs mx-auto">{t.step4Desc}</p>
            </div>
          )}

          {/* Feedback */}
          {error && (
            <div className="mt-4 text-sm text-rose-600 bg-rose-50 rounded-lg px-4 py-2">{error}</div>
          )}
          {success && (
            <div className="mt-4 text-sm text-green-700 bg-green-50 rounded-lg px-4 py-2">{success}</div>
          )}

          {/* Step counter */}
          <p className="text-xs text-gray-400 mt-5 text-center">
            {t.stepOf(step, TOTAL_STEPS)}
          </p>
        </div>

        {/* Footer actions */}
        <div className="px-8 pb-8 flex items-center justify-between gap-3">
          {step > 1 && step < 4 ? (
            <button onClick={handleBack}
              className="flex items-center gap-1 px-4 py-2 border border-gray-200 rounded-lg text-sm font-semibold text-gray-600 hover:bg-gray-50 transition">
              <ChevronLeft className="w-4 h-4" /> {t.backBtn}
            </button>
          ) : <div />}

          {step < 4 ? (
            <div className="flex items-center gap-2">
              {step === 3 && (
                <button onClick={() => setStep(4)}
                  className="px-4 py-2 text-sm font-semibold text-gray-500 hover:text-gray-700 transition">
                  {t.skipBtn}
                </button>
              )}
              <button
                onClick={handleNext}
                disabled={saving || (step === 1 && !orgName.trim())}
                className="flex items-center gap-1.5 px-5 py-2 rounded-lg text-sm font-bold text-white transition disabled:opacity-50"
                style={{ background: G }}
              >
                {step === 3 ? t.inviteBtn : t.nextBtn}
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <button onClick={handleFinish}
              className="flex items-center gap-1.5 px-6 py-2 rounded-lg text-sm font-bold text-white transition"
              style={{ background: G }}>
              {t.finishBtn}
              <ChevronRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default GovernanceOnboardingPage;
